"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import {
  Sparkles, Kanban, Globe, BarChart3, Calendar, Check, ChevronDown, Menu, X,
} from "lucide-react";
import { L } from "./tokens";
import { LogoMark } from "./LogoMark";

type PaneKey = "screening" | "pipeline" | "portal" | "analytics" | "calendar";

const NAV_ITEMS: Array<{
  key: PaneKey;
  icon: React.ElementType;
  accent: "violet" | "green" | "amber";
  title: string;
  sub: string;
  desc: string;
  bullets: { t: string; d: string }[];
}> = [
  {
    key: "screening", icon: Sparkles, accent: "violet",
    title: "Screening inteligente",
    sub: "Subí el CV una vez y Talenv extrae datos, matchea…",
    desc: "Subí el CV una vez y Talenv extrae datos, matchea keywords contra el perfil y rankea al candidato. Las primeras 3 horas de screening manual se vuelven 5 minutos de revisión.",
    bullets: [
      { t: "Extracción automática", d: "Skills, años de experiencia, último puesto, educación y idiomas — del PDF a tu pipeline en segundos." },
      { t: "Keyword matching con evidencia", d: "Definís las palabras clave del puesto y la IA marca cuáles aparecen, con el fragmento del CV donde lo dice." },
      { t: "Rating + resumen", d: "Score de 1 a 5 contra el perfil, resumen breve, fortalezas y brechas claras." },
      { t: "Filtro por fit", d: "Filtrás el pipeline por candidatos ≥85 / 70+ / 60+ y revisás primero a los más alineados." },
    ],
  },
  {
    key: "pipeline", icon: Kanban, accent: "violet",
    title: "Pipeline kanban",
    sub: "Un tablero por búsqueda, otro global con todas",
    desc: "Un tablero por búsqueda, otro global con todas. Arrastrás candidatos entre etapas, dejás notas internas y el historial queda guardado sin que nadie tenga que cargar nada.",
    bullets: [
      { t: "Drag & drop entre etapas", d: "7 etapas listas (Postulado → Contratado) o configurás tus propias para cada búsqueda." },
      { t: "Vista global o por búsqueda", d: "Mirás todo el funnel o te enfocás en un puesto. Filtros por fuente, fit y estado." },
      { t: "Rechazo con razón", d: "Motivo estructurado al rechazar — alimenta los analytics de descarte." },
      { t: "Timeline por aplicación", d: "Quién movió a quién, cuándo, desde dónde — auditable y sin Excel paralelo." },
    ],
  },
  {
    key: "portal", icon: Globe, accent: "green",
    title: "Portal de empleo",
    sub: "Un sitio público con la marca de tu empresa",
    desc: "Un sitio público con la marca de tu empresa donde los candidatos ven las vacantes y se postulan. Cada postulación entra directo al pipeline, sin reenviar mails.",
    bullets: [
      { t: "Branding propio", d: "Logo, colores y mensaje de bienvenida configurables. Tu portal con la identidad visual de tu empresa." },
      { t: "Formulario multi-paso", d: "Datos personales, educación, experiencia, idiomas, preguntas custom del puesto y CV." },
      { t: "Compartir en 1 click", d: "Botones de LinkedIn, WhatsApp, copy-link y descarga de QR para flyers." },
      { t: "Anti-spam con Turnstile", d: "CAPTCHA opcional para frenar postulaciones fake sin fricción para el candidato real." },
    ],
  },
  {
    key: "analytics", icon: BarChart3, accent: "amber",
    title: "Analytics & scorecards",
    sub: "Dashboards de funnel, time-to-hire, fuente y recl…",
    desc: "Dashboards de funnel, time-to-hire, fuente y reclutador. Scorecards estructurados para que las entrevistas dejen de ser una conversación que nadie anota.",
    bullets: [
      { t: "Funnel + tiempos por etapa", d: "Cuántos llegan a cada etapa, mediana / promedio / P90 de days-to-hire." },
      { t: "Efectividad por fuente", d: "Cuál canal trae más contrataciones reales — no solo postulaciones." },
      { t: "Scorecards ponderados", d: "Cada entrevistador puntúa criterios definidos; Talenv calcula el score final con pesos." },
      { t: "Export CSV", d: "Todo lo del dashboard se baja en CSV para llevarlo a tu BI o reportes ejecutivos." },
    ],
  },
  {
    key: "calendar", icon: Calendar, accent: "violet",
    title: "Calendario",
    sub: "Agendá entrevistas con Meet links automáticos",
    desc: "Sincronizá tu Google Calendar y agendá entrevistas en segundos. Talenv crea los eventos, genera Meet links automáticos y manda confirmaciones al candidato. Reagendar es un click.",
    bullets: [
      { t: "Sync con Google Calendar", d: "Conectás tu cuenta personal una vez y las entrevistas que agendás en Talenv aparecen en tu calendario." },
      { t: "Meet links automáticos", d: "Al crear la entrevista Talenv genera el link de Google Meet y lo manda al candidato sin que toques nada." },
      { t: "Confirmación + recordatorios", d: "El candidato recibe mail con los detalles y recordatorios programables antes de la entrevista." },
      { t: "Reagendar en 1 click", d: "Movés la entrevista a otro horario y el candidato recibe la actualización con el nuevo link. Sin idas y vueltas por mail." },
    ],
  },
];

const ACCENT_COLOR: Record<string, string> = { violet: L.violet, green: L.green, amber: L.amber };
const ACCENT_BG: Record<string, string> = { violet: L.violetSoft, green: L.greenSoft, amber: "#FBEBD1" };

type PillRect = { left: number; top: number; width: number; height: number };

export function Nav({ locale }: { locale: string }) {
  const [scrolled, setScrolled] = useState(false);
  const [navUp, setNavUp] = useState(false);
  const [planesActive, setPlanesActive] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);
  const [activePane, setActivePane] = useState<PaneKey>("screening");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pillRect, setPillRect] = useState<PillRect | null>(null);
  const [logoHover, setLogoHover] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const linksWrapRef = useRef<HTMLDivElement>(null);
  const lastYRef = useRef(0);

  // Scroll: navIsland/navShrink (via `scrolled`), navHide (dirección) y
  // navSpy ("Planes" activo cerca de esa sección) — todo en un único
  // listener, igual que el handoff (varios listeners de scroll
  // independientes, acá consolidados).
  // Nota: el handoff también tenía navCtaSwap (el CTA cambia a "Empezá
  // gratis" cerca de pricing) — decisión de producto: en el navbar el CTA
  // siempre dice "Inicia sesión", no se swapea.
  useEffect(() => {
    function onScroll() {
      const y = window.scrollY;
      setScrolled(y > 8);

      if (y > 200 && y > lastYRef.current + 4) setNavUp(true);
      else if (y < lastYRef.current - 4 || y < 200) setNavUp(false);
      lastYRef.current = y;

      const planesSec = document.getElementById("planes");
      if (planesSec) setPlanesActive(planesSec.offsetTop <= y + window.innerHeight * 0.35);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    document.body.style.overflow = "hidden";
    function onKey(e: KeyboardEvent) { if (e.key === "Escape") setMobileOpen(false); }
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [mobileOpen]);

  function openDd() {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setDdOpen(true);
  }
  function scheduleCloseDd() {
    closeTimer.current = setTimeout(() => setDdOpen(false), 80);
  }

  function handlePillEnter(e: React.MouseEvent<HTMLElement>) {
    const wrap = linksWrapRef.current;
    if (!wrap) return;
    const wr = wrap.getBoundingClientRect();
    const r = e.currentTarget.getBoundingClientRect();
    setPillRect({ left: r.left - wr.left, top: r.top - wr.top, width: r.width, height: r.height });
  }
  function handlePillLeave() { setPillRect(null); }

  const active = NAV_ITEMS.find((it) => it.key === activePane) ?? NAV_ITEMS[0];

  return (
    <>
      <nav
        className="sticky top-0 z-50"
        style={{
          transition: "background .3s ease, box-shadow .3s ease, transform .35s cubic-bezier(.22,1,.36,1), margin .3s ease, border-radius .3s ease, max-width .3s ease",
          background: scrolled ? "rgba(250,247,242,0.82)" : "rgba(250,247,242,0.78)",
          backdropFilter: "blur(14px) saturate(1.3)",
          WebkitBackdropFilter: "blur(14px) saturate(1.3)",
          // navIsland: al scrollear, la nav se despega del borde y se
          // angosta a una "isla" flotante con bordes redondeados.
          ...(scrolled
            ? {
                maxWidth: 1080, margin: "10px auto 0", borderRadius: 999,
                border: "1px solid rgba(21,16,61,.08)",
                boxShadow: "0 18px 40px -18px rgba(21,16,61,.28)",
              }
            : { maxWidth: "100%", margin: 0, borderRadius: 0, borderBottom: `1px solid ${L.line}` }),
          // navHide: se oculta al bajar, reaparece al subir.
          transform: navUp ? "translateY(-110%)" : "translateY(0)",
        }}
      >
        <div
          className="mx-auto flex max-w-[1240px] items-center gap-9 px-8"
          style={{ paddingTop: scrolled ? 8 : 14, paddingBottom: scrolled ? 8 : 14, transition: "padding .3s ease" }}
        >
          <Link
            href={`/${locale}`}
            className="flex flex-shrink-0 items-center gap-2"
            style={{ fontFamily: L.fontDisplay, fontWeight: 700, fontSize: 18, color: L.ink, letterSpacing: "-0.02em" }}
            onMouseEnter={() => setLogoHover(true)}
            onMouseLeave={() => setLogoHover(false)}
          >
            <span
              className="inline-flex"
              style={{
                transition: "transform .3s cubic-bezier(.34,1.56,.64,1)",
                transform: logoHover ? "rotate(-8deg) scale(1.1)" : "none",
                transformOrigin: "left center",
              }}
            >
              <span style={{ display: "inline-block", transition: "transform .3s ease", transform: scrolled ? "scale(0.9)" : "none" }}>
                <LogoMark />
              </span>
            </span>
            talenv
          </Link>

          {/* Nav links — ocultos en mobile */}
          <div ref={linksWrapRef} className="relative hidden [@media(min-width:821px)]:flex items-center gap-9 flex-1" onMouseLeave={handlePillLeave}>
            {/* Pill deslizante detrás del link/trigger en hover */}
            <div
              className="pointer-events-none absolute rounded-full"
              style={{
                background: L.violetSoft, zIndex: 0,
                opacity: pillRect ? 1 : 0,
                left: pillRect?.left ?? 0, top: pillRect?.top ?? 0,
                width: pillRect?.width ?? 0, height: pillRect?.height ?? 0,
                transition: "left .28s cubic-bezier(.22,1,.36,1), width .28s cubic-bezier(.22,1,.36,1), top .28s, height .28s, opacity .2s",
              }}
            />

            <div
              className="relative z-[1]"
              onMouseEnter={openDd}
              onMouseLeave={scheduleCloseDd}
            >
              <button
                type="button"
                onMouseEnter={handlePillEnter}
                className="-mx-1 -my-1.5 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[14.5px] font-medium transition-colors"
                style={{ color: ddOpen ? L.ink : L.inkSoft }}
                aria-haspopup="true"
                aria-expanded={ddOpen}
              >
                Producto
                <ChevronDown size={14} style={{ transform: ddOpen ? "rotate(180deg)" : "none", transition: "transform 180ms" }} />
              </button>

              {/* Siempre montado (contenido crawleable) — visibilidad por
                  opacity/pointer-events, no por mount condicional. */}
              <div
                className="absolute left-[-16px] top-[calc(100%+8px)] z-50 flex overflow-hidden rounded-2xl transition-opacity duration-150"
                style={{
                  width: 880, maxWidth: "calc(100vw - 64px)", background: "#fff", border: `1px solid ${L.lineStrong}`, boxShadow: SHADOW_MEGA,
                  opacity: ddOpen ? 1 : 0,
                  visibility: ddOpen ? "visible" : "hidden",
                  pointerEvents: ddOpen ? "auto" : "none",
                }}
              >
                  {/* Lista izquierda */}
                  <div style={{ width: 260, padding: 8, borderRight: `1px solid ${L.line}`, flexShrink: 0 }}>
                    {NAV_ITEMS.map((it) => {
                      const Icon = it.icon;
                      const isActive = it.key === activePane;
                      return (
                        <button
                          key={it.key}
                          type="button"
                          onMouseEnter={() => setActivePane(it.key)}
                          onFocus={() => setActivePane(it.key)}
                          className="flex w-full items-start gap-3 rounded-xl p-2.5 text-left transition-colors"
                          style={{ background: isActive ? L.violetSoft : "transparent" }}
                        >
                          <div className="flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-[9px]" style={{ background: ACCENT_BG[it.accent], color: ACCENT_COLOR[it.accent] }}>
                            <Icon size={15} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div style={{ fontFamily: L.fontDisplay, fontWeight: 700, fontSize: 13.5, color: L.ink, letterSpacing: "-0.01em" }}>{it.title}</div>
                            <div className="line-clamp-2" style={{ fontSize: 11.5, color: L.inkSoft, marginTop: 2, lineHeight: 1.35 }}>{it.sub}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Panel derecho */}
                  <div className="flex-1 min-w-0" style={{ background: L.bg, padding: "18px 20px" }}>
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div className="flex h-8 w-8 items-center justify-center rounded-[9px]" style={{ background: ACCENT_BG[active.accent], color: ACCENT_COLOR[active.accent] }}>
                        <active.icon size={16} />
                      </div>
                      <h4 style={{ fontFamily: L.fontDisplay, fontWeight: 700, fontSize: 17, color: L.ink, margin: 0 }}>{active.title}</h4>
                    </div>
                    <p style={{ fontSize: 12.5, color: L.inkSoft, maxWidth: 540, margin: "0 0 14px", lineHeight: 1.5 }}>{active.desc}</p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                      {active.bullets.map((b) => (
                        <div key={b.t} className="flex items-start gap-2">
                          <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full" style={{ background: `${ACCENT_COLOR[active.accent]}22`, color: ACCENT_COLOR[active.accent] }}>
                            <Check size={10} strokeWidth={3} />
                          </span>
                          <div>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: L.ink }}>{b.t}</div>
                            <div style={{ fontSize: 11.5, color: L.inkSoft, marginTop: 1, lineHeight: 1.4 }}>{b.d}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <a href="#features" onClick={() => setDdOpen(false)} className="mt-3.5 inline-block text-[12.5px] font-semibold" style={{ color: ACCENT_COLOR[active.accent] }}>
                      Ver todos los features →
                    </a>
                  </div>

                  {/* Recursos */}
                  <div className="flex flex-col" style={{ width: 158, borderLeft: `1px solid ${L.line}`, padding: "12px 4px 12px 14px", gap: 2, flexShrink: 0 }}>
                    <div style={{ fontFamily: L.fontMono, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: L.inkFaint, margin: "0 0 8px 10px" }}>Recursos</div>
                    <a href={`/${locale}/help`} className="rounded-[9px] px-2.5 py-2 transition-colors hover:bg-[#FAF7F2]">
                      <b className="block" style={{ fontSize: 12.5, fontWeight: 600, color: L.ink }}>FAQ / Ayuda</b>
                      <small style={{ fontSize: 11, color: L.inkSoft }}>Respuestas rápidas</small>
                    </a>
                    <a href="mailto:hola@talenv.com" className="rounded-[9px] px-2.5 py-2 transition-colors hover:bg-[#FAF7F2]">
                      <b className="block" style={{ fontSize: 12.5, fontWeight: 600, color: L.ink }}>Hablar con nosotros</b>
                      <small style={{ fontSize: 11, color: L.inkSoft }}>hola@talenv.com</small>
                    </a>
                  </div>
              </div>
            </div>

            <a
              href="#planes"
              onMouseEnter={handlePillEnter}
              className="tl-nav-link relative z-[1] -mx-1 -my-1.5 rounded-full px-3 py-1.5 text-[14.5px] font-medium transition-colors"
              style={{ color: planesActive ? L.ink : L.inkSoft }}
            >
              Planes
              <span
                className="tl-nav-underline absolute bottom-[-5px] h-[2px] rounded-full"
                style={{
                  background: L.violet,
                  left: planesActive ? 0 : "50%",
                  right: planesActive ? 0 : "50%",
                  transition: "left .22s cubic-bezier(.22,1,.36,1), right .22s cubic-bezier(.22,1,.36,1)",
                }}
              />
            </a>
          </div>

          <div className="flex-1 [@media(min-width:821px)]:hidden" />

          {/* CTAs — ocultos en mobile */}
          <div className="hidden [@media(min-width:821px)]:flex items-center gap-2 flex-shrink-0">
            <Link href={`/${locale}/plans`} className="inline-flex items-center rounded-full px-3.5 py-2 text-[14.5px] font-medium transition-colors hover:bg-black/[0.04]" style={{ color: L.ink }}>
              Registrate
            </Link>
            <Link
              href={`/${locale}/login`}
              className="inline-flex items-center rounded-full px-4 py-2 text-[14.5px] font-medium text-white transition-transform hover:-translate-y-px"
              style={{ background: L.ink, boxShadow: "0 1px 0 rgba(255,255,255,0.15) inset, 0 6px 18px -8px rgba(21,16,61,0.45)" }}
            >
              Inicia sesión
            </Link>
          </div>

          {/* Hamburger — mobile only */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-lg [@media(min-width:821px)]:hidden"
            style={{ color: L.ink }}
            aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* ============ MOBILE MENU ============ */}
      {mobileOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Menú principal"
          className="fixed inset-0 z-40 overflow-y-auto [@media(min-width:821px)]:hidden"
          style={{ background: L.bg, paddingTop: 68 }}
        >
          <div className="px-6 py-6">
            <div className="mb-2" style={{ fontFamily: L.fontMono, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: L.inkFaint }}>Producto</div>
            <div className="flex flex-col gap-1 mb-6">
              {NAV_ITEMS.map((it) => {
                const Icon = it.icon;
                return (
                  <a
                    key={it.key}
                    href="#features"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-3 rounded-xl px-3 py-2.5"
                  >
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg" style={{ background: ACCENT_BG[it.accent], color: ACCENT_COLOR[it.accent] }}>
                      <Icon size={16} />
                    </span>
                    <span>
                      <span className="block" style={{ fontSize: 14, fontWeight: 600, color: L.ink }}>{it.title}</span>
                      <span className="block" style={{ fontSize: 12, color: L.inkSoft }}>{it.sub}</span>
                    </span>
                  </a>
                );
              })}
            </div>

            <div className="mb-2" style={{ fontFamily: L.fontMono, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: L.inkFaint }}>Talenv</div>
            <div className="flex flex-col gap-1 mb-6">
              <a href="#planes" onClick={() => setMobileOpen(false)} className="rounded-xl px-3 py-2.5" style={{ fontSize: 14, fontWeight: 500, color: L.ink }}>Planes</a>
              <a href={`/${locale}/help`} className="rounded-xl px-3 py-2.5" style={{ fontSize: 14, fontWeight: 500, color: L.ink }}>Preguntas frecuentes</a>
              <a href="mailto:hola@talenv.com" className="rounded-xl px-3 py-2.5" style={{ fontSize: 14, fontWeight: 500, color: L.ink }}>Escribinos</a>
            </div>

            <div className="flex flex-col gap-2.5">
              <Link href={`/${locale}/plans`} className="flex items-center justify-center rounded-[11px] border py-3.5 text-[15.5px] font-medium" style={{ borderColor: L.lineStrong, color: L.ink, background: "#fff" }}>
                Registrate
              </Link>
              <Link href={`/${locale}/login`} className="flex items-center justify-center rounded-[11px] py-3.5 text-[15.5px] font-medium text-white" style={{ background: L.violet }}>
                Inicia sesión
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const SHADOW_MEGA = "0 24px 48px -16px rgba(21,16,61,0.18)";
